<template>
  <div class="w-full flex flex-col min-h-screen">
    <header
      class="bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800"
    >
      <div class="flex flex-row justify-between items-center py-2">
        <div class="flex w-full py-2 px-4">
          <woot-sidemenu-icon class="mt-1" />
          <h1
            class="m-0 text-lg text-slate-900 dark:text-slate-100 whitespace-nowrap mt-1 my-0 mx-2"
          >
            {{ $t('BROADCAST.BROADCAST_MANAGEMENT') }}
          </h1>
        </div>
        <div class="flex gap-2 mr-4">
          <div class="relative">
            <woot-button
              v-tooltip.right="$t('BROADCAST.FILTERS.REFRESH_LABEL')"
              variant="smooth"
              color-scheme="secondary"
              icon="arrow-clockwise"
              size="tiny"
              @click="refreshBroadcasts"
            />
          </div>
          <div class="relative">
            <woot-button
              v-tooltip.right="$t('BROADCAST.FILTERS.FILTER_LABEL')"
              variant="smooth"
              size="tiny"
              color-scheme="secondary"
              class="selector-button"
              icon="sort-icon"
              @click="toggleDropdown"
            />
            <div
              v-if="showActionsDropdown"
              v-on-clickaway="closeDropdown"
              class="dropdown-pane dropdown-pane--open mt-1 right-0 basic-filter gap-y-2"
            >
              <div class="flex flex-row items-center my-2">
                <span class="text-xs w-1/2">{{
                  $t('BROADCAST.FILTERS.ORDER_BY')
                }}</span>
                <select
                  v-model="selectedType"
                  class="bg-slate-25 dark:bg-slate-700 text-xs h-6 my-0 py-0 pr-7 pl-2 w-32 border border-solid border-slate-75 dark:border-slate-600 text-slate-800 dark:text-slate-100"
                  @change="onTypeSelected"
                >
                  <option :value="''">
                    {{ $t('BROADCAST.FILTERS.ALL_LABEL') }}
                  </option>
                  <option
                    v-for="item in broadcastFilterList"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </div>
              <div class="flex flex-row items-center my-2">
                <span class="text-xs w-1/2">{{
                  $t('BROADCAST.FILTERS.INBOX')
                }}</span>
                <select
                  v-model="selectedInbox"
                  class="bg-slate-25 dark:bg-slate-700 text-xs h-6 my-0 py-0 pr-6 pl-2 w-32 border border-solid border-slate-75 dark:border-slate-600 text-slate-800 dark:text-slate-100"
                  @change="onInboxSelected"
                >
                  <option :value="''">
                    {{ $t('BROADCAST.FILTERS.ALL_LABEL') }}
                  </option>
                  <option
                    v-for="item in inboxesList"
                    :key="item.value"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="relative">
            <woot-button
              class="clear"
              color-scheme="success"
              icon="add"
              data-testid="create-new-broadcast"
              @click="createBroadcast"
            >
              {{ $t('BROADCAST.SEND_BROADCAST') }}
            </woot-button>
          </div>
        </div>
      </div>
    </header>
    <section class="conversation-page bg-white dark:bg-slate-900 overflow-auto">
      <div
        v-if="uiFlags.isFetching"
        class="flex justify-center items-center text-center text-slate-700 dark:text-slate-200 py-4"
      >
        <spinner />{{ $t('BROADCAST.LOADING_DATA') }}
      </div>
      <div v-else>
        <div
          v-if="broadcasts.length === 0"
          class="text-center py-4 text-gray-600"
        >
          {{ $t('BROADCAST.NO_DATA_BROADCAST') }}
        </div>
        <div v-else>
          <div class="broadcast-table overflow-auto">
            <ve-table
              max-height="calc(100vh - 8.000rem)"
              scroll-width="50rem"
              row-key-field-name="template_name"
              :columns="columns"
              :table-data="broadcasts"
              :border-around="false"
              :row-style-option="getRowStyleOption()"
            />
          </div>
        </div>
      </div>
    </section>

    <footer
      v-if="meta.allCount > 0"
      class="bg-white dark:bg-slate-800 h-[60px] border-t border-solid border-slate-75 dark:border-slate-700/50 flex items-center justify-between py-0 px-4"
    >
      <div class="left-aligned-wrap">
        <div class="text-xs text-slate-600 dark:text-slate-200">
          <strong>{{ firstIndex }}</strong> -
          <strong>{{ lastIndex }}</strong> of
          <strong>{{ meta.allCount }}</strong> items
        </div>
      </div>
      <div class="right-aligned-wrap flex">
        <div class="primary button-group pagination-button-group items-center">
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="secondary"
            :is-disabled="!hasPrevPage"
            @click="onFirstPage"
          >
            <fluent-icon icon="chevron-left" size="18" />
            <fluent-icon icon="chevron-left" size="18" />
          </woot-button>
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="secondary"
            :is-disabled="!hasPrevPage"
            @click="onPrevPage"
          >
            <fluent-icon icon="chevron-left" size="18" />
          </woot-button>
          <woot-button size="small" variant="smooth" color-scheme="secondary">
            {{ meta.currentPage }}
          </woot-button>
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="secondary"
            :is-disabled="!hasNextPage"
            @click="onNextPage"
          >
            <fluent-icon icon="chevron-right" size="18" />
          </woot-button>
          <woot-button
            size="small"
            variant="smooth"
            color-scheme="secondary"
            :is-disabled="!hasNextPage"
            @click="onLastPage"
          >
            <fluent-icon icon="chevron-right" size="18" />
            <fluent-icon icon="chevron-right" size="18" />
          </woot-button>
        </div>
      </div>
    </footer>
  </div>
</template>
<script>
import { VeTable } from 'vue-easytable';
import { mixin as clickaway } from 'vue-clickaway';
import Spinner from 'shared/components/Spinner.vue';
import { mapGetters } from 'vuex';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

export default {
  components: {
    VeTable,
    Spinner,
  },
  mixins: [clickaway],
  data() {
    return {
      showActionsDropdown: false,
      selectedType: '',
      selectedInbox: '',
    };
  },
  computed: {
    ...mapGetters({
      broadcasts: 'broadcastsView/getBroadcasts',
      meta: 'broadcastsView/getBroadcastsMeta',
      uiFlags: 'broadcastsView/getUiFlags',
      inboxes: 'broadcastsView/getInboxList',
      unread_count: 'broadcastsView/getUnreadCount',
    }),
    inboxesList() {
      return this.inboxes.map(inbox => ({
        value: inbox.id,
        label: inbox.name,
      }));
    },
    broadcastFilterList() {
      return [
        { value: 'pending', label: this.$t('BROADCAST.STATUS.PENDING_EXIST') },
        {
          value: 'no_pending',
          label: this.$t('BROADCAST.STATUS.ALL_COMPLETE'),
        },
      ];
    },
    firstIndex() {
      return (this.meta.currentPage - 1) * this.meta.perPage + 1;
    },
    lastIndex() {
      return Math.min(
        this.firstIndex + this.meta.perPage - 1,
        this.meta.allCount
      );
    },
    hasPrevPage() {
      return this.meta.currentPage > 1;
    },
    hasNextPage() {
      return this.meta.currentPage * this.meta.perPage < this.meta.allCount;
    },
    columns() {
      return [
        {
          field: 'index',
          key: 'index',
          title: 'No',
          width: 10,
          align: 'center',
          fixed: 'left',
          renderBodyCell: ({ rowIndex }) => {
            return (
              (this.meta.currentPage - 1) * this.meta.perPage + (rowIndex + 1)
            );
          },
        },
        {
          field: 'template_name',
          key: 'template_name',
          title: 'Template',
          fixed: 'left',
          width: 30,
          align: 'left',
          renderBodyCell: ({ row }) => {
            const formattedName = row.template_name
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            return (
              <div class="row-template">
                <div class="template-block">{formattedName}</div>
              </div>
            );
          },
        },
        {
          field: 'inbox',
          key: 'inbox',
          title: this.$t('BROADCAST.TABLE.INBOX'),
          fixed: 'left',
          width: 30,
          align: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-template">
                <div class="template-block">
                  <div class="channel-row">
                    <FluentIcon icon={`brand-whatsapp`} />
                    {row.inbox.name}
                  </div>
                </div>
              </div>
            );
          },
        },
        {
          field: 'total_count',
          key: 'total_count',
          title: this.$t('BROADCAST.TABLE.TOTAL_MESSAGE'),
          width: 30,
          align: 'center',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-status-template">
                <div class="status-template-block">{row.total_count}</div>
              </div>
            );
          },
        },
        {
          field: 'pending_count',
          key: 'pending_count',
          title: this.$t('BROADCAST.TABLE.TOTAL_PENDING'),
          width: 30,
          align: 'center',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-status-template">
                <div class="status-template-block">{row.pending_count}</div>
              </div>
            );
          },
        },
        {
          field: 'success_rate',
          key: 'success_rate',
          title: this.$t('BROADCAST.TABLE.SUCCESS_RATE'),
          width: 30,
          align: 'center',
          renderBodyCell: ({ row }) => {
            return (
              <div class="row-status-template">
                <div class="status-template-block">
                  <span>{row.success_rate}%</span>
                  <span class="text-xs text-slate-500">
                    {row.delivered_count} / {row.sent_count}
                  </span>
                </div>
              </div>
            );
          },
        },
        {
          field: 'aksi',
          key: 'aksi',
          title: this.$t('BROADCAST.TABLE.ACTION'),
          width: 25,
          fixed: 'right',
          renderBodyCell: ({ row }) => (
            <div class="justify-center flex gap-4">
              <router-link
                to={`/app/accounts/${this.$route.params.accountId}/broadcasts/${row.template_name}/edit`}
              >
                {this.$t('BROADCAST.TABLE.SHOW')}
              </router-link>
            </div>
          ),
        },
      ];
    },
  },
  methods: {
    createBroadcast() {
      const accountId = this.$route.params.accountId;
      this.$router.push({
        name: 'broadcasts_create',
        params: { accountId },
      });
    },
    getRowStyleOption() {
      return {
        clickHighlight: false,
        stripe: false,
      };
    },
    onTypeSelected() {
      this.fetchBroadcasts();
    },
    onInboxSelected() {
      this.fetchBroadcasts();
    },
    toggleDropdown() {
      this.showActionsDropdown = !this.showActionsDropdown;
    },
    closeDropdown() {
      this.showActionsDropdown = false;
    },
    onPerPageChange() {
      this.meta.currentPage = 1;

      this.fetchBroadcasts();
    },
    onFirstPage() {
      this.meta.currentPage = 1;
      this.fetchBroadcasts();
    },
    onPrevPage() {
      if (this.hasPrevPage) {
        this.meta.currentPage--;

        this.fetchBroadcasts();
      }
    },
    onNextPage() {
      if (this.hasNextPage) {
        this.meta.currentPage++;

        this.fetchBroadcasts();
      }
    },
    onLastPage() {
      this.meta.currentPage = Math.ceil(this.meta.allCount / this.meta.perPage);

      this.fetchBroadcasts();
    },
    refreshBroadcasts() {
      this.$store.dispatch('broadcastsView/getUnreadCount');
      this.fetchBroadcasts();
    },
    fetchBroadcasts() {
      this.$store.dispatch('broadcastsView/get', {
        per_page: this.meta.perPage,
        page: this.meta.currentPage,
        type: this.selectedType,
        inbox_id: this.selectedInbox,
      });
    },
    fetchInboxList() {
      this.$store.dispatch('broadcastsView/getInbox');
    },
  },
  mounted() {
    this.$store.dispatch('broadcastsView/getUnreadCount');
    this.fetchBroadcasts();
    this.fetchInboxList();
  },
};
</script>

<style scoped>
.conversation-page {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100vh;
}

.broadcast-table {
  margin: 0;
  padding: 0;
  width: 100%;
  flex-grow: 1;
  overflow: auto;
}

.row-status-template {
  @apply items-center flex text-center my-4 justify-center;
}

.status-template-block {
  @apply items-center flex flex-col my-0 justify-center;
}

.row-template {
  @apply items-center flex text-left my-4;
}

.template-block {
  @apply items-start flex flex-col my-0;
}

.channel-row {
  @apply flex items-center gap-2;
}

.basic-filter {
  @apply w-52 p-4 top-6;
}
</style>
